import { compressImageForUpload } from "@/utils/imageClientCompress"

/** Balance upload speed vs quality for player submissions (photos/videos to companies). */
export const PLAYER_UPLOAD_IMAGE_MAX_EDGE = 1920
export const PLAYER_UPLOAD_IMAGE_QUALITY = 0.88

/** Target bitrate for in-browser video recording (~2.5 Mbps). */
export const PLAYER_RECORD_VIDEO_BITS_PER_SECOND = 2_500_000
export const PLAYER_RECORD_AUDIO_BITS_PER_SECOND = 128_000

const IMAGE_TYPE_REGEX = /^image\//i
const VIDEO_TYPE_REGEX = /^video\//i

const toFile = (file: File | Blob, fileName: string): File => {
	if (file instanceof File) {
		return file
	}
	return new File([file], fileName, {
		type: file.type || "application/octet-stream",
		lastModified: Date.now(),
	})
}

export const isPlayerImageUpload = (file: File | Blob) =>
	IMAGE_TYPE_REGEX.test(file.type || "") ||
	(!file.type && /\.(jpe?g|png|gif|webp|heic|heif|avif|bmp)$/i.test(file instanceof File ? file.name : ""))

export const isPlayerVideoUpload = (file: File | Blob) =>
	VIDEO_TYPE_REGEX.test(file.type || "") ||
	(!file.type && /\.(mp4|mov|webm|m4v|mkv)$/i.test(file instanceof File ? file.name : ""))

/**
 * Shrinks photos before upload so less data goes over the network.
 * Videos from the gallery are left as-is (recording uses capped bitrate in CameraFullScreen).
 */
export const compressPlayerMediaForUpload = async (
	file: File | Blob,
	fileName = "upload.bin"
): Promise<File> => {
	const asFile = toFile(file, fileName)

	if (!isPlayerImageUpload(asFile)) {
		return asFile
	}

	return compressImageForUpload(asFile, {
		maxWidth: PLAYER_UPLOAD_IMAGE_MAX_EDGE,
		maxHeight: PLAYER_UPLOAD_IMAGE_MAX_EDGE,
		quality: PLAYER_UPLOAD_IMAGE_QUALITY,
		mimeType: "image/jpeg",
	})
}

export const createPlayerVideoRecorder = (stream: MediaStream): MediaRecorder => {
	const bitrates: MediaRecorderOptions = {
		videoBitsPerSecond: PLAYER_RECORD_VIDEO_BITS_PER_SECOND,
		audioBitsPerSecond: PLAYER_RECORD_AUDIO_BITS_PER_SECOND,
	}

	const mimeCandidates = [
		"video/webm;codecs=vp9",
		"video/webm;codecs=vp8",
		"video/webm",
		"video/mp4",
	]

	for (const mimeType of mimeCandidates) {
		if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(mimeType)) {
			return new MediaRecorder(stream, { ...bitrates, mimeType })
		}
	}

	return new MediaRecorder(stream, bitrates)
}
